import React, { FC, useEffect, useState } from 'react'
import { KeyboardAvoidingView, View, Platform, useWindowDimensions, BackHandler } from 'react-native'
import { useIsFocused } from '@react-navigation/native'
import { useFormik } from 'formik'
import * as yup from 'yup'
import Popover from 'react-native-popover-view'
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs'
import { DeviceEventEmitter } from 'react-native'

import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks'
import CommonHeader from '../../shared/commonComponents/commonHeader'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'
import { getAllPosZones, checkDuplicateTaxName, getOnePosTax, createTax, updateTax } from '../../redux/slices/taxSlice'
import { config } from '../../shared/environment'
import { zonedropdownVal, taxZonesMappings, KeyType, TempItem } from './taxModule'
import { ZenButton, ZenLoader, ZenToast } from 'zen-core-elements'
import { useAppNavigation, useAppRoute } from '../../types/@react-navigation/navigation'
import { fontSize, appColors, appFonts } from '../../themes/appTheme'
import TaxDetails from './taxDetails'
import TaxProducts from './taxProducts'
import { shallowEqual } from 'react-redux'

const AddEditTax: FC = () => {
    const dispatch = useAppDispatch()
    const route = useAppRoute("AddEditTax") as any
    const { isWrite: isTaxWrite } = useAppSelector(getScreenPermission("TaxStack"), shallowEqual)
    const params = route.params
    const navigation = useAppNavigation()
    const isFocused = useIsFocused()
    const { width } = useWindowDimensions()

    const TopTab = createMaterialTopTabNavigator()

    const [zoneVal, setZoneVal] = useState<any>([])
    const [nameExist, setNameExist] = useState<string>('')
    const [editData, setEditData] = useState<TempItem[]>([])
    const [leavePopover, setLeavePopover] = useState<boolean>(false)
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

    const onePosTax = useAppSelector(state => state?.tax?.getOnePosTax?.value)
    const duplicateLoader = useAppSelector(state => state?.tax?.checkDuplicateTax?.loading)
    const duplicateValue = useAppSelector(state => state?.tax?.checkDuplicateTax?.value)
    const buttonLoader = useAppSelector(state => state?.tax?.createTax?.loading || state?.tax?.updateTax?.loading)

    const isView = params?.isView ?? false

    // State variable used to store leave page dialogbox props
    const removeProductsProps = {
        content: "You have unsaved changes. Are you sure you want to leave this page?",
        button: [{ id: 1, name: 'No', color: appColors.primary, backgroundColor: appColors.light }, { id: 2, name: 'Yes', backgroundColor: appColors.primary, color: appColors.light }],
        icon: { type: 'material-community', name: 'run', color: appColors.primary }
    }

    // Variable which is used to define validation for form fields
    const validationSchema = yup.object().shape({
        taxName: yup.string()
            .label('taxName')
            .test('Validation', "Tax name doesn't accept space",
                (value) => {
                    if (value) {
                        return value === value?.trim()
                    }
                    return true
                }
            )
            .max(50, 'Tax name should not exceed maximum of 50 characters')
            .required('Manual tax name is required'),
        description: yup.string()
            .label('description')
            .test('Validation', "Please enter valid description",
                (value) => {
                    if (value) {
                        return value === value?.trim()
                    }
                    return true
                }
            )
            .max(250, 'Description should not exceed maximum of 250 characters'),
        otherZoneRate: yup.string()
            .test('Error msg on Minimum zero or Maximum 100', 'Tax percentage accept from 1 to 100%', (val) => {
                return !(val !== undefined && (parseFloat(val) > 100 || parseFloat(val) < 1));
            })
            .matches(/^\d{1,3}([.]\d{1,2}){0,1}$/, 'Tax percentage accept upto 2 decimal places only'),
        isEnabled: yup.boolean(),
        isEnabledAllProducts: yup.boolean(),
        productIds: yup.array(),
        mappingId: yup.array(),
        zones: yup.array().of(
            yup.object().shape({
                rowId: yup.string().nullable(),
                zone: yup.object().shape({
                    zoneEnable: yup.boolean(),
                    zoneName: yup.string().required('zoneName is required').
                        test(
                            "unique-zoneNames",
                            "Zone name already exists",
                            function (val) {
                                const { options } = this;
                                if (!val) return true;

                                const storeZoneNames = (options?.context as { zones?: TempItem[] })?.zones?.map(
                                    (i) => i.zone?.zoneName
                                ) || [];

                                const thisIndex = (options as { index?: number })?.index;
                                const firstOccurrence = storeZoneNames.findIndex(
                                    (name) => name === val
                                );

                                return thisIndex === firstOccurrence;
                            }
                        ),
                }),
                zoneRate: yup.number()
                    .typeError('Enter a valid tax percentage')
                    .required('Tax percentage is required')
                    .min(1, 'Tax Percentages are accepted from 1 to 100%')
                    .max(100, 'Tax Percentages are accepted from 1 to 100%')
                    .test('decimal-places', 'Tax percentage accept upto 2 decimal places only', function (value) {
                        if (value === undefined || value === null) return true;
                        return /^\d+(\.\d{1,2})?$/.test(value.toString());
                    }),
            })
        )
    });

    const formik = useFormik({
        initialValues: {
            description: params?.ele?.id ? onePosTax?.taxCategory?.description! : '',
            isAddProduct: true,
            isEnabled: params?.ele?.id ? onePosTax?.taxCategory?.isEnabled : true,
            isEnabledAllProducts: params?.ele?.id ? onePosTax?.taxCategory?.isEnabledAllProducts! : false,
            isTaxableProducts: [],
            mappingId: [],
            taxName: params?.ele?.id ? onePosTax?.taxCategory?.name! : '',
            otherZoneRate: params?.ele?.id ? onePosTax?.taxCategory?.otherZoneRate ?? "" : "",
            productIds: [],
            statusUpdateOnly: false,
            taxFor: params?.ele?.id ? onePosTax?.taxCategory?.taxFor : 'Specific Zone',
            taxType: 1,
            zipCode: [],
            zones: (params?.ele?.id && editData?.length > 0) ? editData : [{ rowId: null, zone: { id: 0, zoneName: '', medium: '' }, zoneEnable: false, zoneName: '', zoneRate: 0, dropdown: false, label: '', value: '' }],
            medium: params?.ele?.medium ? params?.ele?.medium : null
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (!duplicateValue?.found) {
                addEditTaxProducts(values)
            } else {
                ZenToast({ type: 'warning', message: 'Manual tax name already exists!' })
            }
        }
    })

    useEffect(() => {
        if (isFocused) {
            getOnePosTaxApi().finally(() => {
                setIsInitialLoading(false)
            })
        }
    }, [isFocused])

    useEffect(() => {
        const nameDebounceFunction = setTimeout(() => {
            if (nameExist !== "") {
                checkDuplicateTax(nameExist)
            }
        }, 500)
        return () => clearTimeout(nameDebounceFunction)
    }, [nameExist])

    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            if (formik?.dirty && isFocused) {
                setLeavePopover(true)
                return true
            } else {
                navigation.goBack()
            }
            return true
        });
        return () => backHandler?.remove()
    }, [formik?.values, formik?.dirty, isFocused])

    useEffect(() => {
        const TaxProductsApi = DeviceEventEmitter.addListener("TaxProductsApi", (productIds: number[]) => {
            addEditTaxProducts(formik?.values, productIds, true)
        })
        return () => {
            TaxProductsApi?.remove()
        }
    }, [formik])

    const checkDuplicateTax = async (val: string) => {
        let data = {
            taxId: params?.ele?.id ? params?.ele?.id : 0,
            taxName: val
        }
        await dispatch(checkDuplicateTaxName({ storeId: config?.storeId, data: data })).unwrap().catch((err) => {
            console.log('Error in duplicate tax', err);
        })
    }

    const getOnePosTaxApi = async () => {
        setZoneVal([])
        try {
            const zone = await dispatch(getAllPosZones({ storeId: config?.storeId })).unwrap()
            const updatedKeys = zone?.zones?.rows?.map((item: zonedropdownVal) => ({
                label: item?.zoneName,
                value: item?.id,
                key: item
            })) || []
            setZoneVal((prevKeys: KeyType[]) => [...prevKeys, ...updatedKeys])

            if (params?.ele?.id) {
                const res = await dispatch(getOnePosTax({ storeId: config?.storeId, id: params?.ele?.id })).unwrap()
                if (res) {
                    let temp: TempItem[] = []
                    res?.taxCategory?.taxZonesMappings?.map((val: taxZonesMappings) => {
                        if (val?.isForZoneMap) {
                            zone?.zones?.rows?.map((item: zonedropdownVal) => {
                                if (val?.zoneId === item?.id)
                                    temp?.push({ rowId: val?.id, zone: item, zoneEnable: val?.isEnabled, zoneName: item?.zoneName, zoneRate: val?.taxPercent, dropdown: false, label: '', value: '', })
                            })
                        }
                    })
                    setEditData(temp)
                }
            }
        } catch (err) {
            console.log('Error in getOnePosTaxApi', err)
        }
    }

    const addEditTaxProducts = async (values: typeof formik.values, selectedProducts?: number[], isevent?: boolean) => {
        let data = {
            id: params?.ele?.id ? params?.ele?.id : null,
            storeId: config?.storeId,
            name: values?.taxName,
            description: values?.description,
            isEnabledAllProducts: values?.isEnabledAllProducts,
            taxFor: "Specific Zone",
            taxType: values?.taxType,
            otherZoneRate: values?.otherZoneRate ? values?.otherZoneRate : null,
            isEnabled: values?.isEnabled,
            // The input keeps the typed text; the API gets the number, as before
            zones: values?.zones?.map((zone: TempItem) => ({ ...zone, zoneRate: Number(zone?.zoneRate) })),
            statusUpdateOnly: false,
            taxId: params?.ele?.id ? params?.ele?.id : 0,
            productIds: selectedProducts || [],
            isAllProductsEnabled: values?.isEnabledAllProducts,
            mappingId: [],
            isTaxableProducts: selectedProducts || [],
            isAddProduct: !!selectedProducts?.length,
            medium: values?.medium ? values?.medium : "POS",
        }
        if (params?.ele?.id) {
            dispatch(updateTax({ storeId: config?.storeId, data: data })).unwrap().then((res) => {
                if (res && res?.taxCategories) {
                    DeviceEventEmitter.emit("TAX_PRODUCTS_UPDATED")
                    ZenToast({ type: "success", message: "Tax was updated successfully" })
                    navigation.pop(isevent ? 2 : 1)
                }
            }).catch((err) => {
                console.log('Error in updateTaxCategories', err)
            })
        }
        else {
            await dispatch(createTax({ storeId: config?.storeId, data: data })).unwrap().then((res) => {
                if (res && res?.taxCategories) {
                    DeviceEventEmitter.emit("TAX_PRODUCTS_UPDATED")
                    ZenToast({ type: "success", message: "Tax created successfully" })
                    navigation.pop(isevent ? 2 : 1)
                }
            }).catch((err) => {
                console.log("Error in create tax", err)
            })
        }
    }

    const leaveConfirmation = (value: { name: string }) => {
        if (value?.name === 'Yes') {
            setLeavePopover(false)
            navigation.goBack()
        } else {
            setLeavePopover(false)
        }
    }

    return (
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} enabled style={{ flex: 1 }}>
            <CommonHeader
                headerName={params?.ele?.id ? "Edit Manual Tax" : "Create a Manual Tax"}
                backIcon={true}
                rightComponent={
                    !isView ? (
                        <ZenButton
                            title={params?.ele?.id ? "Update" : "Save"}
                            disabled={!formik?.dirty || buttonLoader}
                            buttonStyle={{ borderRadius: 10, backgroundColor: appColors.primary }}
                            titleStyle={{ color: appColors.light, fontSize: fontSize.mediumFontSize, fontFamily: appFonts.bold }}
                            onPress={() => { formik.handleSubmit() }}
                        />
                    ) : <></>
                }
                backIconOnpress={() => { formik?.dirty ? setLeavePopover(true) : navigation.goBack() }}
            />
            {isInitialLoading ?
                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <ZenLoader lottieType="Local" lottie={require("../../assets/infinityLoader.json")} />
                </View> :
                <View style={{ flex: 1, backgroundColor: appColors.lightBackgroundColor }}>
                    <TopTab.Navigator
                        initialRouteName="Tax"
                        screenOptions={{
                            lazy: true,
                            tabBarLabelStyle: { fontSize: fontSize.largerFontSize, fontFamily: appFonts.bold, textTransform: 'capitalize' },
                            tabBarIndicatorStyle: { backgroundColor: appColors.primary },
                            tabBarActiveTintColor: appColors.primary,
                            tabBarInactiveTintColor: appColors.grey,
                            tabBarAndroidRipple: { borderless: false },
                            swipeEnabled: false
                        }}
                        backBehavior="none"
                    >
                        <TopTab.Screen
                            name="Tax"
                            options={{ tabBarLabel: 'Tax Details', title: "Tax Details" }}
                            children={() => <TaxDetails formik={formik} zoneVal={zoneVal} isView={isView} duplicateLoader={duplicateLoader} duplicateValue={duplicateValue} setNameExist={setNameExist} taxId={params?.ele?.id} navigation={navigation} />}
                        />
                        <TopTab.Screen
                            name="Products"
                            options={{ tabBarLabel: 'Product Details', title: "Product Details" }}
                            component={TaxProducts}
                            initialParams={{ taxId: params?.ele?.id || 0, isView, isAllProduct: false, formikValues: formik.values }}
                            listeners={{
                                tabPress: (e) => {
                                    if (!formik.values.taxName?.trim() || duplicateValue?.found) {
                                        e.preventDefault()
                                        ZenToast({ type: "warning", message: "Please enter a manual tax name." })
                                    }
                                },
                            }}
                        />
                    </TopTab.Navigator>
                </View>}

            <Popover isVisible={leavePopover} popoverStyle={{ width: width > 900 ? width / 2 : width > 600 ? 450 : 350, borderRadius: 10 }}>
                <CommonDialogBox type="OTHER" dialog={removeProductsProps} confirmation={leaveConfirmation} />
            </Popover>
        </KeyboardAvoidingView>
    )
}

export default AddEditTax